<script setup lang="ts">
import { ref, watch, inject, computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import { IInvoice } from '@posx/core';
import { timelineService, TimelineResponse } from '@/services/timeline.service';

const { t } = useI18n();
const currentInvoice = inject<Ref<IInvoice | null>>('invoice', ref(null));
const $emit = defineEmits(['update:modelValue']);
const $props = defineProps<{ modelValue?: boolean }>();

const visible = ref($props.modelValue);
const loading = ref(false);
const error = ref<string | null>(null);
const timeline = ref<TimelineResponse | null>(null);

async function fetchTimeline(): Promise<void> {
  if (!currentInvoice?.value?.uid) return;
  loading.value = true;
  error.value = null;
  try {
    timeline.value = await timelineService.getTimeline(currentInvoice.value.uid);
  } catch (e: any) {
    error.value = e?.response?.status === 404 ? t('timeline.not_found') : t('timeline.load_error');
  } finally {
    loading.value = false;
  }
}

watch(() => $props.modelValue, (newVal) => {
  visible.value = !!newVal;
  if (newVal) fetchTimeline();
});

const handleClose = () => { $emit('update:modelValue', false); };
const formatTime = (utc: string) => dayjs(utc).format('h:mm:ss A');
const orderDate = computed(() => {
  const src = timeline.value?.events?.[0]?.utc || currentInvoice?.value?.created_at;
  return src ? dayjs(src).format('D MMM YYYY') : '';
});
</script>
<template>
  <x-dialog v-model="visible" width="770px" :title="t('timeline.title')" titleClass="text-[20px] font-medium tracking-tight text-[#353849]" :close-on-click-modal="false" @close="handleClose">
    <div v-loading="loading" class="w-full h-full flex flex-col overflow-hidden">
      <template v-if="!loading && !error && timeline">
        <div class="ordtl-meta"><template v-if="orderDate"><b>{{ orderDate }}</b> · </template>{{ t('timeline.table') }}: <b>{{ timeline.table }}</b> · {{ t('timeline.server') }}: <b>{{ timeline.server }}</b> · {{ t('timeline.ref') }}: <b>{{ timeline.ref_id }}</b> · {{ t('timeline.status') }}: <b>{{ timeline.status }}</b></div>
        <div class="ordtl-list">
          <div v-for="(event, idx) in timeline.events" :key="idx" class="ordtl-row">
            <span class="ordtl-time">{{ formatTime(event.utc) }}</span>
            <span class="ordtl-thread"><span class="ordtl-dot" /></span>
            <span class="ordtl-body">
              <span class="ordtl-title">{{ event.title }}<span v-if="event.employee" class="ordtl-emp">{{ event.employee }}</span><span v-if="event.device && !event.device.startsWith('dev_')" class="ordtl-device">{{ event.device }}</span></span>
              <span v-if="event.detail" class="ordtl-detail">{{ event.detail }}</span>
            </span>
          </div>
        </div>
      </template>
      <template v-if="!loading && error">
        <div class="flex-1 flex items-center justify-center">
          <span class="text-[#6b7088]">{{ error }}</span>
        </div>
        <x-button type="primary" class="w-[355px] shrink-0 self-center" @click="fetchTimeline">{{ t('common.retry') }}</x-button>
      </template>
    </div>
  </x-dialog>
</template>
<style scoped lang="scss">
.ordtl-meta { flex-shrink: 0; font-size: 15px; font-weight: 500; color: #4A4E63; padding: 2px 2px 10px; background: #fff; }
.ordtl-meta b { font-weight: 700; color: #2B2D3A; }
.ordtl-list { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; scrollbar-width: none; }
.ordtl-list::-webkit-scrollbar { display: none; }
.ordtl-row { position: relative; display: grid; grid-template-columns: 96px 20px 1fr; }
.ordtl-row + .ordtl-row { border-top: 1px dashed #eef0f5; }
.ordtl-time { font-size: 13px; font-weight: 600; color: #52599D; font-variant-numeric: tabular-nums; padding: 13px 0; }
.ordtl-thread { position: relative; display: flex; justify-content: center; }
.ordtl-thread::before { content: ""; position: absolute; top: 0; bottom: 0; width: 2px; background: #e1e3ed; }
.ordtl-row:first-child .ordtl-thread::before { top: 50%; }
.ordtl-row:last-child .ordtl-thread::before { bottom: 50%; }
.ordtl-dot { position: relative; z-index: 1; margin-top: 18px; width: 8px; height: 8px; flex-shrink: 0; border-radius: 50%; background: #6a72b2; box-shadow: 0 0 0 3px #fff; }
.ordtl-body { display: flex; flex-direction: column; gap: 3px; padding: 13px 4px 13px 0; }
.ordtl-title { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 15px; font-weight: 600; color: #2B2D3A; }
.ordtl-emp { font-size: 12px; font-weight: 600; color: #52599D; background: #EEF0FF; border-radius: 999px; padding: 2px 9px; }
.ordtl-device { font-size: 12px; font-weight: 600; color: #6b7088; background: #eef0f5; border-radius: 999px; padding: 2px 9px; }
.ordtl-detail { font-size: 13px; color: #6b7088; line-height: 1.4; }
</style>
